import { Buffer } from 'buffer';
import { Injectable } from '@angular/core';
import { ModeOfOperation, utils as AesJsUtils } from 'aes-js';
import { pad, unpad } from 'pkcs7';

import { AppConstants } from '@app/app.constant';

@Injectable({
    providedIn: 'root',
})
export class AESEncryptionService {
    private readonly keyBuffer: Buffer;
    private readonly  ivBuffer: Buffer;

    constructor() {
        // Caching the keyBuffer and ivBuffer to avoid repetitive conversion
        this.keyBuffer = Buffer.from(AppConstants.aesKey, 'base64'); // Converting key from base64 to buffer
        this.ivBuffer = Buffer.from(AppConstants.aesIV, 'base64'); // Converting iv from base64 to buffer
    }

    /**
     * Encrypt data into base64 (after multiple conversions)
     * @param valueToEncrypt : value should be a string
     * @return : encrypted string in base64
     */
    public encrypt(valueToEncrypt: string) {
        const aesCbc = new ModeOfOperation.cbc(this.keyBuffer, this.ivBuffer);

        const textBytes = AesJsUtils.utf8.toBytes(valueToEncrypt); // Converting text to utf8 format bytes
        const encryptedBytes = aesCbc.encrypt(pad(textBytes)); // Encrypting utf8 bytes
        const encryptedAsciBytes = Buffer.from(encryptedBytes); // Converting utf8 bytes to buffer

        return encryptedAsciBytes.toString('base64'); // Converting buffer to base64 text
    }

    /**
     * Decrypt data from base64 (after multiple conversions)
     * @param valueToDecrypt : encrypted base64 string
     * @return : decrypted plain text
     */
    public decrypt(valueToDecrypt: string) {
        const aesCbc = new ModeOfOperation.cbc(this.keyBuffer, this.ivBuffer);

        const encryptedBytes = Buffer.from(valueToDecrypt, 'base64'); // Converting text to utf8 format bytes
        const decryptedBytes = unpad(aesCbc.decrypt(encryptedBytes)); // Decrypting utf8 format bytes
        return AesJsUtils.utf8.fromBytes(decryptedBytes); // Converting utf8 bytes to string
    }
}
